import {
  FlexibleConnectedPositionStrategyOrigin,
  Overlay,
  OverlayModule,
  OverlayRef,
} from '@angular/cdk/overlay';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatInput, MatInputModule } from '@angular/material/input';
import { PortalModule, TemplatePortal } from '@angular/cdk/portal';
import {
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { Subscription, skip } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import {
  ALL_COUNTRIES,
  ALL_OS,
  AUDIT_LOG_ACTIONS,
  CtxFormMode,
  Filter,
  FilterComparisonOperator,
  OPERATORS_FOR_FILTER_TYPE,
  SegmentResourceName,
  compareFilterArrays,
  getFiltersFromUrl,
  getLabelForComparisonOperator,
  getResourceDisplayName,
  originalOrder,
  projectEnv,
} from 'utils';
import type { ResourceTableService } from 'libs/ng-ui/src/lib/_services/resource-table.service';
import {
  DataCacheService,
  getLabelForFilter,
} from 'libs/ng-ui/src/lib/_services/data-cache.service';
import { SegmentService } from 'libs/ng-ui/src/lib/_services/segment.service';
import { CommonModule } from '@angular/common';
import { CtxButtonGroupComponent } from 'ng-ui';
import { MatOptionModule } from '@angular/material/core';
import { CtxCustomerSearchComponent } from '../customer-search/customer-search.component';
import { CtxButtonComponent } from '../button/button.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AutocompleteSearchPipe } from '../../_pipes/autocomplete-search.pipe';
import {
  ResourceListFn,
  ResourceReadFn,
} from '../../_services/resource.service';
import { CtxDatepickerToggleComponent } from '../datepicker-toggle/datepicker-toggle.component';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';

@Component({
  selector: 'ctx-table-filter',
  templateUrl: './table-filter.component.html',
  standalone: true,
  imports: [
    CommonModule,
    CtxButtonGroupComponent,
    MatInputModule,
    ReactiveFormsModule,
    MatOptionModule,
    CtxCustomerSearchComponent,
    CtxButtonComponent,
    MatAutocompleteModule,
    MatTooltipModule,
    MatDatepickerModule,
    AutocompleteSearchPipe,
    OverlayModule,
    PortalModule,
    CtxDatepickerToggleComponent,
    MatSelectModule,
    MatMenuModule,
  ],
})
export class CtxTableFilterComponent implements OnInit, OnDestroy {
  ALL_COUNTRIES = ALL_COUNTRIES;

  getResourceDisplayName = (
    resourceName: string // material displayWidth accepts only one parameter
  ) => {
    return getResourceDisplayName(resourceName, projectEnv.get('projectName'));
  };

  // Icons

  originalOrder = originalOrder;
  /*
   * Input gets created inside overlay via displayFilter() and autofocus attr (to makes input active) works only on load()
   * setTimeOut() used to make it happen
   */
  @ViewChild('propertyField', { read: MatInput }) filterPropertyField: MatInput;

  /**
   * Reference to the filterForm template. This is used to render the form inside the Overlay.
   */
  @ViewChild('filterFormTemplate') filterFormTemplate: TemplatePortal<any>;

  /**
   * ResourceTableService instance used to make modifications to the table data
   */
  @Input({ required: true }) resourceTableService: ResourceTableService;

  /** The active segment resource-name  */
  @Input() activeSegment: SegmentResourceName;

  /** Configuration for the filter if User/Org searching is required  */
  @Input() searchConfig: {
    userRead?: ResourceReadFn<any>;
    userList?: ResourceListFn<any>;
    orgRead?: ResourceReadFn<any>;
    orgList?: ResourceListFn<any>;
    resellerRead?: ResourceReadFn<any>;
    resellerList?: ResourceListFn<any>;
  };

  /**
   *We've restrain ourselves from CtxForm because
   *Our form need only basic functionalities like patchValue, basic validation etc
   */
  filterForm: FormGroup;

  /**
   * We use a single ngTemplate in this component to generate our form. This saves us from writing about
   * 12kB of templating twice for creation form and updation form. However, this forces us to define the
   * type of action being done on the form. This is kept only to keep track between functions of the component.
   */
  filterFormStatus: { type: CtxFormMode; filterIndex: number };

  /**
   * Enum to access in the template
   */
  CtxFormMode = CtxFormMode;

  /**
   * Reference to Overlay element. This is used across the component to make sure only one overlay is being worked on at once.
   */
  overlayRef: OverlayRef;

  /**
   * List of all operators related to particular paramaterName formControl value
   */
  OPERATORS_FOR_FILTER_TYPE = OPERATORS_FOR_FILTER_TYPE;

  /** Subscription to active segment in SegmentService. */
  activeSegment$: Subscription;

  SEGMENT_HELP_TITLE = 'Save filter';

  constructor(
    private fb: FormBuilder,
    private overlayService: Overlay,
    public segmentService: SegmentService,
    private route: ActivatedRoute,
    public dataCache: DataCacheService
  ) {}

  /**
   * To check save segement button availability, which becomes active on certain change inside generatedFilters[]
   */
  get showSaveSegmentButton(): boolean {
    if (
      this.resourceTableService.actions.segments &&
      this.resourceTableService.filters.length > 0 &&
      !this.isSegmentPure
    ) {
      return true;
    } else {
      return false;
    }
  }

  /** Dirty checks if current filters are equal to segment filters  */
  get isSegmentPure() {
    return compareFilterArrays(
      this.resourceTableService.filters,
      this.segmentService.activeSegment$.value.filters
    );
  }

  get isResourceFilterable(): boolean {
    if (
      Object.keys(this.resourceTableService.filterableProperties).length === 0
    ) {
      return false;
    } else {
      return true;
    }
  }

  ngOnInit() {
    if (this.resourceTableService.actions.segments && this.activeSegment) {
      this.segmentService.activeResource = this.activeSegment;
    }

    this.filterForm = this.fb.group({
      property: ['', Validators.required],
      operator: [
        new FormControl<FilterComparisonOperator>('eq'),
        Validators.required,
      ],
      value: ['', Validators.required],
      displayValue: [''],
    });

    /**
     * WARNING
     * Subscribe to changes to segment. The async anonymous function within the subscription is not executed as
     * an async function.
     *
     * Workarounds to this problem like mergeMap, concatMap, and switchMap have been unsuccessful, probably due to
     * incorrect implementations but currently, as a workaround, we have skipped the first value, i.e. the
     * 'All' segment. This prevents any unusual behaviour with QueryParam filters.
     *
     * We can still expect this piece of code to have side-effects and unexpected behaviour, and skipping the first
     * value is only the best workaround at the moment. This prevents the redundant Web API call as well.
     */
    this.activeSegment$ = this.segmentService.activeSegment$
      .pipe(skip(1))
      .subscribe(async (segment) => {
        this.resourceTableService.filters = [...segment.filters];
      });

    this.setExistingFiltersIfPresent();
  }

  /**
   * Sets existing filters from service/route to the component.
   * This enables a consistent view between table and filter component.
   */
  private async setExistingFiltersIfPresent() {
    // IF query parameters contain a TableFilter
    const _routeFilters = getFiltersFromUrl(this.route);

    if (_routeFilters) {
      this.resourceTableService.filters = _routeFilters;
    }
  }

  getLabelForComparisonOperator = getLabelForComparisonOperator;
  getLabelForFilter = getLabelForFilter;
  ALL_OS = ALL_OS;
  AUDIT_LOG_ACTIONS = AUDIT_LOG_ACTIONS;
  /**
   * Submits the filterForm, stops execution if form is invalid
   */
  async submit() {
    if (this.filterForm.invalid) {
      return;
    } else {
      this.filterForm
        .get('value')
        ?.setValue(
          this.trimFilterValue(
            this.filterForm.value.value,
            this.filterForm.value.operator
          )
        );
      this.submitFilterForm();
      this.overlayRef.detach();
    }
  }
  trimFilterValue(value: unknown, operator: FilterComparisonOperator) {
    if (typeof value !== 'string') {
      return value;
    } else if (operator === 'in' || operator === 'nin') {
      const getArrayFromValue = value.split(',').map((item) => {
        return item.trim();
      });
      return getArrayFromValue
        .filter((item) => {
          return item;
        })
        .join(',');
    } else {
      return value.trim();
    }
  }
  /**
   * Uses filterFormStatus property to check whether the action is
   */
  private submitFilterForm() {
    if (this.filterFormStatus.type === CtxFormMode.Create) {
      this.addFilter(this.filterForm.value);
    } else {
      this.mutateFilterValue();
    }
  }

  /**
   * Uses the current state of the form to add a new filter to the active filters.
   */
  private addFilter(tableFilter: Filter) {
    this.resourceTableService.filters = [
      ...this.resourceTableService.filters,
    ].concat(tableFilter);
  }

  /**
   * Uses the current state of the form to update the active filter as set by the openFilterUpdateForm
   */
  private mutateFilterValue() {
    // Check if value is number, this is done so the expression does not become false for 0
    if (!isNaN(this.filterFormStatus.filterIndex)) {
      // Copy array of filters
      const arr = [...this.resourceTableService.filters];
      // Set the filter at the index to the new value
      arr[this.filterFormStatus.filterIndex] = this.filterForm.getRawValue();
      // Update filters in the service
      this.resourceTableService.filters = arr;
    }
  }
  /**
   * Removes a filter from the active filters. Sets the filter request query params.
   * @param index index of filter
   */
  async removeFilter(index: number) {
    // Copy array of filters
    const filterCopy = [...this.resourceTableService.filters];
    // Remove array element at index
    filterCopy.splice(index, 1);
    // Update filters in the service
    this.resourceTableService.filters = filterCopy;
  }

  /**
   * Creates overlay based on filter clicked
   * @param data filter data available
   */
  openFilterUpdateForm(mouseEvent: MouseEvent, index: number) {
    const filterObject = this.resourceTableService.filters[index];
    this.filterForm.reset();
    this.filterForm.patchValue(filterObject);

    this.filterFormStatus = {
      type: CtxFormMode.Update,
      filterIndex: index,
    };

    this.openFilterForm(mouseEvent);
  }

  /**
   * Opens filter form overlay for creation
   */
  openFilterCreateForm(mouseEvent: MouseEvent) {
    this.filterForm.reset();
    this.filterForm.patchValue({ operator: 'eq' });

    this.filterFormStatus = {
      type: CtxFormMode.Create,
      filterIndex: 0,
    };
    this.openFilterForm(mouseEvent);

    // If property is not filled, assign keyboard focus to the field
    setTimeout(() => {
      if (this.filterPropertyField && !this.filterPropertyField.value) {
        this.filterPropertyField.focus();
      }
    });
  }

  /**
   * To trigger the filter overlay for filter query entries.
   */
  private openFilterForm(origin: FlexibleConnectedPositionStrategyOrigin) {
    this.overlayRef = this.overlayService.create({
      hasBackdrop: true,
      maxWidth: '40ch',
      positionStrategy: this.overlayService
        .position()
        .flexibleConnectedTo(origin)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
            offsetY: 3,
          },
        ])
        /**
         * Sets a minimum distance the overlay may be positioned to the edge of the viewport.
         */
        .withViewportMargin(8),
    });
    this.overlayRef.addPanelClass([
      'ctx-filter__connected-overlay',
      'mat-elevation-z8',
      'p-4',
      'rounded-md',
      'w-full',
    ]);
    // Render filterForm in the overlay
    this.overlayRef.attach(this.filterFormTemplate);

    // Close overlay when clicked outside
    const backdropClickSubscription = this.overlayRef
      .backdropClick()
      .subscribe(() => {
        this.overlayRef.detach();
        backdropClickSubscription.unsubscribe();
      });
  }

  ngOnDestroy() {
    this.activeSegment$.unsubscribe();
  }

  /**
   * Checks the filterablePropertyType for the supplied param `property` and returns an array of operators based on that.
   *
   * @param property Property for which the supported Operators are returned
   */
  get comparisonOperators() {
    const filterPropertyType =
      this.resourceTableService.filterableProperties[
        this.filterForm.value.property
      ];
    const operators = this.OPERATORS_FOR_FILTER_TYPE[filterPropertyType];
    if (operators && operators.length > 0) {
      return operators;
    } else {
      return null;
    }
  }
  /**
   *  Updates the operator value when the user selects an operator from the autocomplete list
   */
  updateFilterOperatorList() {
    this.filterForm
      .get('operator')
      ?.setValue(this.comparisonOperators ? this.comparisonOperators[0] : 'eq');
    const filterPropertyType =
      this.resourceTableService.filterableProperties[
        this.filterForm.value.property
      ];
    if (filterPropertyType === 'boolean') {
      this.filterForm.get('value')?.setValue('true');
    } else {
      this.filterForm.get('value')?.setValue('');
    }
  }

  /** Quick fix for date related issues */
  handleDateSelection(event: MatDatepickerInputEvent<Date>) {
    if (event.value) {
      this.filterForm.patchValue({ value: event.value.toISOString() });
    }
  }

  /** Set the display value */
  setDisplayValue(displayValue: string) {
    this.filterForm.patchValue({ displayValue });
  }
}
